// Game modes and target scores
export type GameMode = '2_player' | '3_player' | '4_player';
export type TargetScore = 501 | 701 | 1001;
export type GameStatus = 'active' | 'completed';

// Trump (Adut) suits
export type TrumpSuit = 'hearts' | 'diamonds' | 'clubs' | 'spades';

// Match format (best of N games)
export type MatchFormat = 'best_of_1' | 'best_of_3' | 'best_of_5';
export type MatchStatus = 'active' | 'completed';

// Player entity
export interface Player {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
}

// Team entity (for 4-player mode)
export interface Team {
  id: string;
  name: string;
  player1Id: string;
  player2Id: string;
  createdAt: Date;
  updatedAt: Date;
}

// Team with player details (for display)
export interface TeamWithPlayers extends Team {
  player1: Player;
  player2: Player;
}

// Participant can be a Player or Team depending on game mode
export interface Participant {
  id: string;
  type: 'player' | 'team';
  name: string;
  // For teams, includes player references
  playerIds?: string[];
  // Reference to saved team (for 4-player mode with predefined teams)
  teamId?: string;
}

// Round score for a single participant
export interface RoundScore {
  participantId: string;
  regularPoints: number;
  declarationPoints: number;
}

// A single round in a game
export interface Round {
  id: string;
  gameId: string;
  roundNumber: number;
  trumpSuit: TrumpSuit;
  trumpCallerId: string;
  scores: RoundScore[];
  createdAt: Date;
}

// Match entity (contains multiple games)
export interface Match {
  id: string;
  format: MatchFormat;
  mode: GameMode;
  targetScore: TargetScore;
  status: MatchStatus;
  participants: Participant[];
  winnerId?: string;
  createdAt: Date;
  completedAt?: Date;
}

// Match with games and calculated wins
export interface MatchWithGames extends Match {
  games: Game[];
  gameWins: Record<string, number>; // participantId -> wins count
  gamesNeededToWin: number;
  currentGame?: GameWithTotals; // Active game if any
}

// Game entity (belongs to a match)
export interface Game {
  id: string;
  matchId: string;
  gameNumber: number;
  mode: GameMode;
  targetScore: TargetScore;
  status: GameStatus;
  participants: Participant[];
  winnerId?: string;
  createdAt: Date;
  completedAt?: Date;
}

// Game with computed totals (for display)
export interface GameWithTotals extends Game {
  rounds: Round[];
  totals: ParticipantTotal[];
}

// Computed total for a participant
export interface ParticipantTotal {
  participantId: string;
  participantName: string;
  regularPoints: number;
  declarationPoints: number;
  totalPoints: number;
}

// Player statistics
export interface PlayerStats {
  playerId: string;
  playerName: string;
  matchesPlayed: number;
  matchesWon: number;
  matchWinRate: number;
  gamesPlayed: number;
  gamesWon: number;
  gameWinRate: number;
  totalPoints: number;
  totalRegularPoints: number;
  totalDeclarationPoints: number;
  averagePointsPerGame: number;
  averageRegularPointsPerGame: number;
  averageDeclarationPointsPerGame: number;
  byMode: {
    mode: GameMode;
    matchesPlayed: number;
    matchesWon: number;
    gamesPlayed: number;
    gamesWon: number;
  }[];
}

// Team statistics
export interface TeamStats {
  teamId: string;
  teamName: string;
  player1Name: string;
  player2Name: string;
  matchesPlayed: number;
  matchesWon: number;
  matchWinRate: number;
  gamesPlayed: number;
  gamesWon: number;
  gameWinRate: number;
  totalPoints: number;
  totalRegularPoints: number;
  totalDeclarationPoints: number;
  averagePointsPerGame: number;
  averageRegularPointsPerGame: number;
  averageDeclarationPointsPerGame: number;
}

// Match configuration for setup
export interface MatchConfig {
  format: MatchFormat;
  mode: GameMode;
  targetScore: TargetScore;
  participants: Participant[];
}

// Form types
export interface NewPlayerForm {
  name: string;
}

export interface NewTeamForm {
  name: string;
  player1Id: string;
  player2Id: string;
}

export interface ScoreEntryForm {
  scores: {
    participantId: string;
    regularPoints: number;
    declarationPoints: number;
  }[];
}

// Validation result
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// Match format configurations
export const MATCH_FORMAT_CONFIG: Record<
  MatchFormat,
  {
    label: string;
    shortLabel: string;
    gamesToWin: number;
    maxGames: number;
  }
> = {
  best_of_1: {
    label: 'Best of 1',
    shortLabel: 'Bo1',
    gamesToWin: 1,
    maxGames: 1,
  },
  best_of_3: {
    label: 'Best of 3',
    shortLabel: 'Bo3',
    gamesToWin: 2,
    maxGames: 3,
  },
  best_of_5: {
    label: 'Best of 5',
    shortLabel: 'Bo5',
    gamesToWin: 3,
    maxGames: 5,
  },
};

// Game mode configurations
export const GAME_MODE_CONFIG: Record<
  GameMode,
  {
    label: string;
    participantCount: number;
    participantType: 'player' | 'team';
    defaultTarget: TargetScore;
    allowedTargets: TargetScore[];
  }
> = {
  '2_player': {
    label: '2 Players',
    participantCount: 2,
    participantType: 'player',
    defaultTarget: 501,
    allowedTargets: [501, 701, 1001],
  },
  '3_player': {
    label: '3 Players',
    participantCount: 3,
    participantType: 'player',
    defaultTarget: 701,
    allowedTargets: [701, 1001],
  },
  '4_player': {
    label: '4 Players',
    participantCount: 2,
    participantType: 'team',
    defaultTarget: 1001,
    allowedTargets: [1001],
  },
};

// Scoring constants
export const SCORING = {
  MAX_REGULAR_POINTS: 162,
  MAX_DECLARATION_POINTS: 570,
  POINTS_SUM_CONSTRAINT: 162, // For 3-player and 4-player modes
} as const;

// Helper function to get games needed to win a match
export function getGamesToWin(format: MatchFormat): number {
  return MATCH_FORMAT_CONFIG[format].gamesToWin;
}

// Helper function to check if a participant has won the match
export function checkMatchWinner(
  gameWins: Record<string, number>,
  gamesToWin: number
): string | null {
  for (const [participantId, wins] of Object.entries(gameWins)) {
    if (wins >= gamesToWin) {
      return participantId;
    }
  }
  return null;
}
